import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import { TextField } from "@/components/base/FormControls";
import { callUsersApi } from "@/lib/usersApi";
import { useToast } from "@/context/ToastContext";

interface AdminModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export default function AdminModal({ open, onClose, onSaved }: AdminModalProps) {
  const toast = useToast();
  const [fullName, setFullName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFullName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setError(null);
  }, [open]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!fullName.trim() || !email.trim()) {
      setError("El nombre y el correo son obligatorios.");
      return;
    }
    if (password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }

    setSubmitting(true);
    const result = await callUsersApi({
      action: "create_admin",
      full_name: fullName.trim(),
      last_name: lastName.trim(),
      email: email.trim(),
      password,
    });
    setSubmitting(false);

    if (!result.ok) {
      setError(result.error ?? "No se pudo crear el administrador.");
      return;
    }
    toast.success("Administrador creado correctamente.");
    onSaved();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title="Nuevo administrador"
      subtitle="El administrador accede con correo y contraseña desde la pantalla de inicio de sesión."
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="admin-form"
            disabled={submitting}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-70"
          >
            {submitting ? <i className="ri-loader-4-line animate-spin text-base"></i> : null}
            Crear administrador
          </button>
        </>
      }
    >
      <form id="admin-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label="Nombre"
            name="full_name"
            value={fullName}
            onChange={setFullName}
            placeholder="Ana"
            required
          />
          <TextField
            label="Apellido"
            name="last_name"
            value={lastName}
            onChange={setLastName}
            placeholder="Gómez"
          />
        </div>
        <TextField
          label="Correo electrónico"
          name="email"
          type="email"
          value={email}
          onChange={setEmail}
          placeholder="admin@rkc.com"
          inputMode="email"
          autoComplete="off"
          required
        />
        <TextField
          label="Contraseña"
          name="password"
          type="password"
          value={password}
          onChange={setPassword}
          placeholder="Mínimo 6 caracteres"
          autoComplete="new-password"
          required
        />
        {error ? (
          <div className="animate-shake flex items-start gap-2.5 rounded-md border border-primary-300 bg-primary-50 px-3.5 py-3 text-sm text-primary-800">
            <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
              <i className="ri-error-warning-line text-base"></i>
            </span>
            <span className="flex-1 leading-snug">{error}</span>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}